import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { obtenerPrestamoPorId, anularPrestamo } from '../../api/common';
import Swal from 'sweetalert2';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  ListGroup,
  Image,
  Tabs,
  Tab
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaEdit,
  FaFilePdf,
  FaBan,
  FaCheckCircle,
  FaClock,
  FaUser,
  FaBox,
  FaCalendar,
  FaClipboard,
  FaSignature,
  FaImage,
  FaFileAlt
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { descargarPDFPrestamo } from './GenerarPdfPrestamo';

const DetallePrestamo = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [prestamo, setPrestamo] = useState(null);

  useEffect(() => {
    cargarPrestamo();
  }, [id]);

  const cargarPrestamo = async () => {
    try {
      setLoading(true);
      const resp = await obtenerPrestamoPorId(id);

      if (resp.status === 200) {
        setPrestamo(resp.data.data);
      } else {
        Swal.fire('Error', 'No se pudo cargar el préstamo', 'error');
        navigate('/prestamos');
      }
    } catch (error) {
      console.error('Error al cargar préstamo:', error);
      Swal.fire('Error', 'Ocurrió un error al cargar el préstamo', 'error');
      navigate('/prestamos');
    } finally {
      setLoading(false);
    }
  };

  // Obtener badge de estado
  const getBadgeEstado = estado => {
    const estados = {
      activo: { bg: 'warning', text: 'Activo', icon: <FaClock /> },
      confirmado: { bg: 'info', text: 'Confirmado', icon: <FaCheckCircle /> },
      devuelto_parcial: { bg: 'primary', text: 'Dev. Parcial', icon: <FaBox /> },
      devuelto: { bg: 'success', text: 'Devuelto', icon: <FaCheckCircle /> },
      anulado: { bg: 'danger', text: 'Anulado', icon: <FaBan /> }
    };

    const config = estados[estado] || { bg: 'secondary', text: estado, icon: null };
    return (
      <Badge bg={config.bg} className="fs-6">
        {config.icon && <span className="me-1">{config.icon}</span>}
        {config.text}
      </Badge>
    );
  };


    // Exportar a PDF
  const handleExportarPDF = () => {
    try {
      const resultado = descargarPDFPrestamo(prestamo);
      
      if (resultado.ok) {
        Swal.fire('¡Éxito!', resultado.message, 'success');
      } else {
        Swal.fire('Error', resultado.message, 'error');
      }
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      Swal.fire('Error', 'No se pudo generar el PDF', 'error');
    }
  };


  // Anular préstamo
  const handleAnular = async () => {
    const { value: motivo } = await Swal.fire({
      title: '¿Anular préstamo?',
      html: `
        <p>Acta: <strong>${prestamo.numeroActa}</strong></p>
        <p class="text-danger">Esta acción no se puede deshacer.</p>
      `,
      input: 'textarea',
      inputLabel: 'Motivo de anulación',
      inputPlaceholder: 'Ingrese el motivo de la anulación...',
      inputValidator: value => {
        if (!value) {
          return 'Debe ingresar un motivo';
        }
      },
      showCancelButton: true,
      confirmButtonText: 'Sí, anular',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545'
    });

    if (motivo) {
      try {
        const resp = await anularPrestamo(prestamo._id, motivo);

        if (resp.ok) {
          Swal.fire('¡Anulado!', 'El préstamo ha sido anulado', 'success');
          cargarPrestamo(); // Recargar datos
        } else {
          Swal.fire(
            'Error',
            resp.message || 'No se pudo anular el préstamo',
            'error'
          );
        }
      } catch (error) {
        console.error('Error al anular préstamo:', error);
        Swal.fire('Error', 'Ocurrió un error al anular el préstamo', 'error');
      }
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando detalle del préstamo...</p>
      </Container>
    );
  }

  if (!prestamo) {
    return (
      <Container className="py-5">
        <Alert variant="danger">No se pudo cargar la información del préstamo</Alert>
      </Container>
    );
  }

  console.log('Prestamo cargado:', prestamo);
  // Calcular estadísticas de equipos
  const totalEquipos = prestamo.equipos.length;
  const equiposPrestados = prestamo.equipos.filter(eq => eq.estado === 'prestado')
    .length;
  const equiposDevueltos = prestamo.equipos.filter(eq => eq.estado === 'devuelto')
    .length;

  return (
    <Container fluid className="py-4 w-100">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate('/prestamos')}
            className="mb-2"
          >
            <FaArrowLeft className="me-2" />
            Volver
          </Button>
          <h2 className="mb-0">
            <FaFileAlt className="me-2 text-primary" />
            Detalle del Préstamo
          </h2>
          <p className="text-muted">Información completa del acta de préstamo</p>
        </Col>
        <Col xs="auto">
          <Button
            variant="outline-primary"
            className="me-2"
            onClick={handleExportarPDF}
          >
            <FaFilePdf className="me-2" />
            Exportar PDF
          </Button>
          {prestamo.estado !== 'anulado' && prestamo.estado !== 'devuelto' && (
            <>
              <Button
                variant="outline-warning"
                className="me-2"
                onClick={() => navigate(`/prestamos/editar/${prestamo._id}`)}
              >
                <FaEdit className="me-2" />
                Editar
              </Button>
              <Button variant="outline-danger" onClick={handleAnular}>
                <FaBan className="me-2" />
                Anular
              </Button>
            </>
          )}
        </Col>
      </Row>

      {/* Información General */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card className="shadow-sm mb-4 w-100">
            <Card.Header className="bg-primary text-white w-100">
              <h5 className="mb-0">
                <FaClipboard className="me-2" />
                Información General
              </h5>
            </Card.Header>
            <Card.Body className="w-100">
              <Row>
                <Col md={6}>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <strong>Número de Acta:</strong>
                      <br />
                      <span className="fs-5 text-primary">
                        {prestamo.numeroActa}
                      </span>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Orden de Trabajo:</strong>
                      <br />
                      {prestamo.numeroOt || prestamo.ot?.NumeroOt || 'N/A'}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>
                        <FaUser className="me-2" />
                        Creado por:
                      </strong>
                      <br />
                      {prestamo.nombreUsuarioCreacion}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>
                        <FaCalendar className="me-2" />
                        Fecha de Creación:
                      </strong>
                      <br />
                      {new Date(prestamo.fechaCreacion).toLocaleString()}
                    </ListGroup.Item>
                  </ListGroup>
                </Col>
                <Col md={6}>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <strong>Estado:</strong>
                      <br />
                      {getBadgeEstado(prestamo.estado)}
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Total de Equipos:</strong>
                      <br />
                      <Badge bg="info" className="fs-6">
                        {totalEquipos}
                      </Badge>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Equipos Prestados:</strong>
                      <br />
                      <Badge bg="warning" className="fs-6">
                        {equiposPrestados}
                      </Badge>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <strong>Equipos Devueltos:</strong>
                      <br />
                      <Badge bg="success" className="fs-6">
                        {equiposDevueltos}
                      </Badge>
                    </ListGroup.Item>
                  </ListGroup>
                </Col>
              </Row>

              {prestamo.observaciones && (
                <Alert variant="info" className="mt-3 mb-0">
                  <strong>Observaciones:</strong>
                  <br />
                  {prestamo.observaciones}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Timeline de Estados */}
        <Col lg={4}>
          <Card className="shadow-sm mb-4 w-100">
            <Card.Header className="bg-info text-white w-100">
              <h5 className="mb-0">
                <FaClock className="me-2" />
                Linea de Tiempo
              </h5>
            </Card.Header>
            <Card.Body className="w-100">
              <ListGroup variant="flush">
                {/* Creación */}
                <ListGroup.Item className="border-0">
                  <div className="d-flex align-items-start">
                    <div className="me-3">
                      <div
                        className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                        style={{ width: '40px', height: '40px' }}
                      >
                        <FaClipboard />
                      </div>
                    </div>
                    <div>
                      <strong>Préstamo Creado</strong>
                      <br />
                      <small className="text-muted">
                        {new Date(prestamo.fechaCreacion).toLocaleString()}
                      </small>
                      <br />
                      <small>{prestamo.nombreUsuarioCreacion}</small>
                    </div>
                  </div>
                </ListGroup.Item>

                {/* Confirmación de entrega */}
                {prestamo.confirmacionEntrega?.confirmado && (
                  <ListGroup.Item className="border-0">
                    <div className="d-flex align-items-start">
                      <div className="me-3">
                        <div
                          className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FaCheckCircle />
                        </div>
                      </div>
                      <div>
                        <strong>Entrega Confirmada</strong>
                        <br />
                        <small className="text-muted">
                          {new Date(
                            prestamo.confirmacionEntrega.fecha
                          ).toLocaleString()}
                        </small>
                        <br />
                        <small>{prestamo.confirmacionEntrega.nombreTecnico}</small>
                      </div>
                    </div>
                  </ListGroup.Item>
                )}

                {/* Recepción en oficina */}
                {prestamo.recepcionOficina?.fechaRecepcion && (
                  <ListGroup.Item className="border-0">
                    <div className="d-flex align-items-start">
                      <div className="me-3">
                        <div
                          className="rounded-circle bg-info text-white d-flex align-items-center justify-content-center"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FaBox />
                        </div>
                      </div>
                      <div>
                        <strong>Recepción en Oficina</strong>
                        <br />
                        <small className="text-muted">
                          {new Date(
                            prestamo.recepcionOficina.fechaRecepcion
                          ).toLocaleString()}
                        </small>
                        <br />
                        <small>
                          {prestamo.recepcionOficina.nombreUsuarioRecibe}
                        </small>
                      </div>
                    </div>
                  </ListGroup.Item>
                )}

                {/* Anulado */}
                {prestamo.estado === 'anulado' && (
                  <ListGroup.Item className="border-0">
                    <div className="d-flex align-items-start">
                      <div className="me-3">
                        <div
                          className="rounded-circle bg-danger text-white d-flex align-items-center justify-content-center"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FaBan />
                        </div>
                      </div>
                      <div>
                        <strong>Préstamo Anulado</strong>
                        <br />
                        <small className="text-muted">
                          {new Date(prestamo.updatedAt).toLocaleString()}
                        </small>
                      </div>
                    </div>
                  </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs de Información Detallada */}
      <Row>
        <Col>
          <Card className="shadow-sm w-100">
            <Card.Body className="w-100">
              <Tabs defaultActiveKey="equipos" className="mb-3">
                {/* Tab Equipos */}
                <Tab eventKey="equipos" title={`Equipos (${totalEquipos})`}>
                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <Table striped hover responsive>
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '80px' }}>Foto</th>
                          <th>Inventario</th>
                          <th>Descripción</th>
                          <th>Serie</th>
                          <th>Marca</th>
                          <th>Modelo</th>
                          <th>Ubicación Anterior</th>
                          <th>Estado</th>
                          <th>Fecha Devolución</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prestamo.equipos.map((eq, index) => (
                          <tr key={index}>
                            <td>
                              {eq.foto || eq.patron?.foto ? (
                                <Image
                                  src={eq.foto || eq.patron.foto}
                                  alt={eq.snapshotEquipo?.item || eq.item}
                                  style={{
                                    width: '50px',
                                    height: '50px',
                                    objectFit: 'cover'
                                  }}
                                  rounded
                                  onError={e => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <FaImage
                                  style={{ fontSize: '30px', color: '#ccc' }}
                                />
                              )}
                            </td>
                            <td>
                              <strong>
                                {eq.snapshotEquipo?.inventario || eq.codigo || eq.inventario || '-'}
                              </strong>
                            </td>
                            <td>{eq.snapshotEquipo?.item || eq.descripcion || '-'}</td>
                            <td>{eq.snapshotEquipo?.serie || eq.serie || '-'}</td>
                            <td>{eq.snapshotEquipo?.marca || '-'}</td>
                            <td>{eq.snapshotEquipo?.modelo || '-'}</td>
                            <td>
                              <Badge bg="secondary">{eq.ubicacionAnterior}</Badge>
                            </td>
                            <td>
                              {eq.estado === 'devuelto' ? (
                                <Badge bg="success">Devuelto</Badge>
                              ) : (
                                <Badge bg="warning">Prestado</Badge>
                              )}
                            </td>
                            <td>
                              {eq.fechaDevolucion
                                ? new Date(eq.fechaDevolucion).toLocaleDateString()
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Tab>

                {/* Tab Confirmación de Entrega */}
                {prestamo.confirmacionEntrega?.confirmado && (
                  <Tab
                    eventKey="confirmacion"
                    title={
                      <>
                        <FaCheckCircle className="me-2" />
                        Confirmación de Entrega
                      </>
                    }
                  >
                    <Card className="border-success w-100">
                      <Card.Body className="w-100">
                        <Row>
                          <Col md={6}>
                            <h6>
                              <FaUser className="me-2" />
                              Técnico que confirmó
                            </h6>
                            <p className="fs-5">
                              {prestamo.confirmacionEntrega.nombreTecnico}
                            </p>

                            <h6 className="mt-3">
                              <FaCalendar className="me-2" />
                              Fecha y Hora
                            </h6>
                            <p>
                              {new Date(
                                prestamo.confirmacionEntrega.fecha
                              ).toLocaleString()}
                            </p>

                            {prestamo.confirmacionEntrega.observaciones && (
                              <>
                                <h6 className="mt-3">Observaciones</h6>
                                <Alert variant="info">
                                  {prestamo.confirmacionEntrega.observaciones}
                                </Alert>
                              </>
                            )}
                          </Col>
                          <Col md={6}>
                            {prestamo.confirmacionEntrega.firma && (
                              <div>
                                <h6>
                                  <FaSignature className="me-2" />
                                  Firma Digital
                                </h6>
                                <Image
                                  src={prestamo.confirmacionEntrega.firma}
                                  alt="Firma del técnico"
                                  fluid
                                  className="border"
                                  style={{ maxHeight: '200px' }}
                                />
                              </div>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Tab>
                )}

                {/* Tab Recepción en Oficina */}
                {prestamo.recepcionOficina?.fechaRecepcion && (
                  <Tab
                    eventKey="recepcion"
                    title={
                      <>
                        <FaBox className="me-2" />
                        Recepción en Oficina
                      </>
                    }
                  >
                    <Card className="border-info w-100">
                      <Card.Body className="w-100">
                        <Row>
                          <Col md={6}>
                            <h6>
                              <FaUser className="me-2" />
                              Recibido por
                            </h6>
                            <p className="fs-5">
                              {prestamo.recepcionOficina.nombreUsuarioRecibe}
                            </p>

                            <h6 className="mt-3">
                              <FaCalendar className="me-2" />
                              Fecha y Hora
                            </h6>
                            <p>
                              {new Date(
                                prestamo.recepcionOficina.fechaRecepcion
                              ).toLocaleString()}
                            </p>

                            <h6 className="mt-3">Estado de Recepción</h6>
                            <p>
                              {prestamo.recepcionOficina.recibidoCompleto ? (
                                <Badge bg="success" className="fs-6">
                                  Devolución Completa
                                </Badge>
                              ) : (
                                <Badge bg="warning" className="fs-6">
                                  Devolución Parcial
                                </Badge>
                              )}
                            </p>

                            {prestamo.recepcionOficina.observaciones && (
                              <>
                                <h6 className="mt-3">Observaciones</h6>
                                <Alert variant="info">
                                  {prestamo.recepcionOficina.observaciones}
                                </Alert>
                              </>
                            )}
                          </Col>
                          <Col md={6}>
                            {prestamo.recepcionOficina.firma && (
                              <div>
                                <h6>
                                  <FaSignature className="me-2" />
                                  Firma Digital
                                </h6>
                                <Image
                                  src={prestamo.recepcionOficina.firma}
                                  alt="Firma de recepción"
                                  fluid
                                  className="border"
                                  style={{ maxHeight: '200px' }}
                                />
                              </div>
                            )}
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Tab>
                )}
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DetallePrestamo;